import {
  Table as MuiTable,
  TableHead as MuiTableHead,
  TableCell as MuiTableCell,
  TableRow as MuiTableRow,
  TableBody as MuiTableBody,
  SvgIcon,
  Typography,
  TableFooter,
  TablePagination,
} from '@mui/material';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from '@Components/Checkbox';
import FullScreenLoader from '@Components/FullScreenLoader';
import MenuWithOptions from '@Components/MenuWithOptions';
import TablePaginationActions from '@Components/TablePaginationActions';
import Icons from '@Constants/Icons.constants';
import { CommonConstants } from '@Constants/index';

import {
  StyledHead,
  StyledTableContainer,
  CheckboxCell,
  LoaderRow,
  PaginationStyles,
} from './Table.styles';
import { TableHead, TableProps, TableRow } from './Table.types';

const Table: FunctionComponent<TableProps> = (
  props: TableProps
): JSX.Element => {
  const {
    loading,
    checked,
    setChecked,
    verticalAlign,
    rows,
    tableHead,
    checkboxMenuProps,
    paginationProps,
  } = props;
  const { page, rowsPerPage, ...resPaginationProps } = paginationProps;

  /** Hooks */
  const { t } = useTranslation();

  /**
   * Returns Table Head Data Alignment according to passed align value.
   */
  const getTableHeadAlignment = (
    align?: 'left' | 'right' | 'center' | 'inherit'
  ): string => {
    switch (align) {
      case 'right':
        return '0 0 0 auto';
      case 'center':
        return '0 auto';
      default:
        return '0';
    }
  };

  /**
   * Handles Click on Table Cell
   *
   * To check/uncheck the table checkbox.
   */
  const tableCellClickHandler = (id: string): void => {
    if (checked && setChecked) {
      const checkedIndex = checked.indexOf(id);
      let newChecked: readonly string[] = [];

      if (checkedIndex === -1) {
        newChecked = newChecked.concat(checked, id);
      } else if (checkedIndex === 0) {
        newChecked = newChecked.concat(checked.slice(1));
      } else if (checkedIndex === checked.length - 1) {
        newChecked = newChecked.concat(checked.slice(0, -1));
      } else if (checkedIndex > 0) {
        newChecked = newChecked.concat(
          checked.slice(0, checkedIndex),
          checked.slice(checkedIndex + 1)
        );
      }

      setChecked(newChecked);
    }
  };

  /**
   * Checks if passed checkbox id is in checked state by checking its id
   * in the `checked` state array.
   */
  const isChecked = (id: string): boolean => checked?.indexOf(id) !== -1;
  return (
    <>
      <StyledTableContainer>
        <MuiTable>
          {/* TABLE HEAD */}
          <MuiTableHead>
            <MuiTableRow className="tableHead-row">
              {checkboxMenuProps && (
                <CheckboxCell>
                  <MenuWithOptions {...checkboxMenuProps} />
                </CheckboxCell>
              )}
              {tableHead.map(
                (
                  { align, width, label, icon, onClick }: TableHead,
                  index: number
                ): JSX.Element => (
                  <MuiTableCell
                    key={`tableHead-${index}`}
                    align={align}
                    sx={{
                      minWidth: `${width || 0}rem`,
                      wordBreak: 'break-word',
                    }}
                  >
                    <StyledHead
                      role="button"
                      tabIndex={onClick && 0}
                      sx={{
                        margin: getTableHeadAlignment(align),
                        cursor: onClick ? 'pointer' : 'initial',
                      }}
                      onClick={onClick}
                      onKeyPress={(e) => {
                        if (onClick && e.key === CommonConstants.ENTER_KEY) {
                          onClick();
                        }
                      }}
                    >
                      <Typography variant="tableHeader">{label}</Typography>
                      {icon && <SvgIcon>{Icons[icon]}</SvgIcon>}
                    </StyledHead>
                  </MuiTableCell>
                )
              )}
            </MuiTableRow>
          </MuiTableHead>
          {/* TABLE HEAD */}
          {/* TABLE BODY */}
          <MuiTableBody sx={{ position: 'relative' }}>
            <>
              {rows.map((row: TableRow): JSX.Element => {
                const { id, data, disabled, checkboxElement } = row;
                const checkedState = isChecked(id);

                return (
                  <MuiTableRow
                    key={id}
                    className={
                      disabled ? 'disabledRow tableBody-row' : 'tableBody-row'
                    }
                    sx={{
                      height: '5.6rem',
                      verticalAlign: verticalAlign || 'middle',
                    }}
                  >
                    {checkboxMenuProps && (
                      <CheckboxCell>
                        {checkboxElement || (
                          <Checkbox
                            checked={checkedState}
                            disabled={disabled}
                            onClick={() => tableCellClickHandler(id)}
                          />
                        )}
                      </CheckboxCell>
                    )}
                    {Object.keys(data).map(
                      (key: string, idx: number): JSX.Element => (
                        <MuiTableCell
                          sx={{
                            maxWidth: `${tableHead[idx].width || 0}rem`,
                            wordBreak: 'break-word',
                          }}
                          key={`tableCell-${idx}`}
                          align={tableHead[idx].align}
                        >
                          {data[key]}
                        </MuiTableCell>
                      )
                    )}
                  </MuiTableRow>
                );
              })}
              {loading && (
                <LoaderRow>
                  <MuiTableCell sx={{ width: '100%', height: '100%', p: '0' }}>
                    <FullScreenLoader LoaderProps={{ size: '4rem' }} />
                  </MuiTableCell>
                </LoaderRow>
              )}
              {/* Text to show when no data is present */}
              {!rows.length && (
                <MuiTableRow className="tableBody-row">
                  <MuiTableCell colSpan={tableHead.length + 1}>
                    <Typography
                      variant="main"
                      fontWeight="bold"
                      textAlign="center"
                      sx={{ my: 3.2, color: 'action.disabled' }}
                    >
                      {t('common.table.No data to show')}
                    </Typography>
                  </MuiTableCell>
                </MuiTableRow>
              )}
            </>
          </MuiTableBody>
          {/* TABLE BODY */}
          {/* TABLE FOOTER */}
          <TableFooter>
            <MuiTableRow sx={{ backgroundColor: 'background.paper' }}>
              <MuiTableCell sx={{ p: 0.05 }} colSpan={tableHead.length + 1} />
            </MuiTableRow>
          </TableFooter>
          {/* TABLE FOOTER */}
        </MuiTable>
      </StyledTableContainer>
      {!loading && !!rows.length && (
        <TablePagination
          labelRowsPerPage={t('common.table.Rows per page')}
          page={page}
          rowsPerPage={rowsPerPage}
          {...resPaginationProps}
          component="div"
          sx={PaginationStyles}
          ActionsComponent={TablePaginationActions}
          SelectProps={{
            variant: 'outlined',
            MenuProps: {
              transformOrigin: {
                vertical: -2,
                horizontal: 37.5,
              },
            },
          }}
        />
      )}
    </>
  );
};

export default Table;
